import { faRepeat } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { HealthFormType } from "api/schemas/UserCourse";
import { PrimaryButton, SecondaryButton } from "shared/atoms/Button";
import { CheckBox } from "shared/atoms/inputs/CheckBox";
import { MUISelect } from "shared/atoms/MUI";

import type { UserCourseView } from "../UserCourseScreen";

import { RadioButtons } from "./RadioButtons";
import { TopBar, TopLeft, TopRight } from "./SharedStyledComponents";

export const UserCourseEditorSummary = ({
  onSaveUserCourse,
  onActivate,
  onChangeView,
  view,
  exercisesPerDay,
  onChangeExercisesPerDay,
  functionalTest,
  onChangeFunctionalTest,
  healthFormType,
  name,
  onNameChange,
  showNameInput,
  nbrOfExercises = 0,
  nbrOfLessons = 0,
  saveButtonLabel,
  saveButtonDisabled = false,
  onNewCourse,
}: {
  onSaveUserCourse: () => void;
  onChangeView: (view: UserCourseView) => void;
  view: UserCourseView;
  exercisesPerDay: number;
  onChangeExercisesPerDay: (n: number) => void;
  functionalTest: boolean;
  onChangeFunctionalTest: (b: boolean) => void;
  healthFormType: HealthFormType | undefined;
  name: string;
  onNameChange: (v: string) => void;
  showNameInput?: boolean;
  nbrOfExercises?: number;
  nbrOfLessons?: number;
  saveButtonLabel: string;
  onActivate?: () => void;
  saveButtonDisabled?: boolean;
  onNewCourse?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <TopBar>
        <TopLeftLast>
          <DetailsTable>
            <tbody>
              {showNameInput && (
                <Row>
                  <TitleTd>{t("usercourse.template_name")}</TitleTd>
                  <td>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={name}
                      onChange={e => onNameChange(e.target.value)}
                      placeholder={t("usercourse.template_name") || "Name"}
                      sx={{ marginBottom: 2 }}
                    />
                  </td>
                </Row>
              )}
              <Row>
                <TitleTd>{t("usercourse.exercise_per_day")}</TitleTd>
                <td>
                  <MUISelect
                    fullWidth
                    options={[
                      { label: "1", value: 1 },
                      { label: "2", value: 2 },
                      { label: "3", value: 3 },
                      { label: "4", value: 4 },
                      { label: "5", value: 5 },
                      { label: "6", value: 6 },
                    ]}
                    value={exercisesPerDay}
                    onChange={event => {
                      onChangeExercisesPerDay(event.target.value as number);
                    }}
                  />
                </td>
              </Row>
              <Row>
                <TitleTd>{`${t("usercourse.function_test")}:`}</TitleTd>
                <td>
                  {" "}
                  <CheckBox
                    name="functional-test-checkbox"
                    label=""
                    checked={functionalTest}
                    onChange={() => {
                      onChangeFunctionalTest(!functionalTest);
                    }}
                  />
                </td>
              </Row>
              <Row>
                <TitleTd>{`${t("usercourse.health_form_type")}:`}</TitleTd>
                <td>
                  <Title>{t(`usercourse.healthformtype.${healthFormType || "generic"}`)}</Title>
                </td>
              </Row>
            </tbody>
          </DetailsTable>
        </TopLeftLast>
        <TopRightLast>
          <TopRightOptions>
            <ButtonContainer>
              {!onActivate && (
                <PrimaryButton
                  disabled={(showNameInput && !name) || saveButtonDisabled}
                  onClick={() => {
                    onSaveUserCourse();
                  }}
                >
                  {saveButtonLabel}
                </PrimaryButton>
              )}
              {onActivate && (
                <>
                  <SecondaryButton
                    disabled={(showNameInput && !name) || saveButtonDisabled}
                    onClick={() => {
                      onSaveUserCourse();
                    }}
                  >
                    {saveButtonLabel}
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={() => {
                      onActivate();
                    }}
                  >
                    {t("usercourse.mark_as_ready_cta")}
                  </PrimaryButton>
                </>
              )}
            </ButtonContainer>
            {onNewCourse && (
              <TopRight>
                <TopLink
                  onClick={() => {
                    onNewCourse();
                  }}
                >
                  <FontAwesomeIcon icon={faRepeat} />
                  {t("usercourse.create_course_new")}
                </TopLink>
              </TopRight>
            )}
          </TopRightOptions>
        </TopRightLast>
      </TopBar>
      <VertSpace>
        <RadioButtons
          options={[
            { label: `${t("usercourse.exercises")} (${nbrOfExercises})`, value: "exercises" },
            { label: `${t("usercourse.lessons")} (${nbrOfLessons})`, value: "lessons" },
          ]}
          value={view}
          onChange={value => {
            onChangeView(value);
          }}
          pillForm
        />
      </VertSpace>
    </>
  );
};

const VertSpace = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const DetailsTable = styled.table`
  margin-bottom: 20px;
`;

const TitleTd = styled.td`
  padding-right: 10px;
`;

const Title = styled.span`
  padding-right: 10px;
`;

const Row = styled.tr`
  height: 30px;
`;

const TopLink = styled.div`
  color: ${props => props.theme.colors.redesign.b100};
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  margin-right: 8px;
`;

const TopRightOptions = styled.div`
  display: flex;
  flex-direction: column;
  ${props => props.theme.belowBreakpoint} {
    flex-grow: 1;
    align-items: center;
    margin-bottom: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-bottom: 16px;
  align-self: flex-start;
  ${props => props.theme.belowBreakpoint} {
    align-self: center;
    flex-direction: column;
  }
`;

const TopLeftLast = styled(TopLeft)`
  ${props => props.theme.belowBreakpoint} {
    order: 2;
  }
`;

export const TopRightLast = styled(TopRight)`
  ${props => props.theme.belowBreakpoint} {
    order: 1;
  }
`;
